import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Calendar, BookOpen, Clock, 
  UserCheck, Download, Pill, ShieldAlert, Utensils, 
  MessageSquare, AlertCircle, BarChart3, PieChart, 
  LogOut, Lock, CheckCircle2, Search, UserMinus, Calculator, FileText, TrendingUp, Trash2
} from 'lucide-react';
import './App.css';

// --- Personal Badge Component ---
const PersonalBadge = () => (
  <div
    id="personal-brand-badge"
    data-testid="personal-badge"
    className="personal-badge"
    style={{
      position: 'fixed',
      bottom: '16px',
      right: '16px',
      zIndex: 99999,
      padding: '10px 16px',
      background: 'rgba(24, 24, 27, 0.85)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '6px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.5px',
      color: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'default',
      userSelect: 'none',
    }}
  >
    <svg 
      width="14" 
      height="14" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      style={{ opacity: 0.8 }}
    >
      <path d="M16 18l2-2v-3a2 2 0 0 0-2-2h-1V9a4 4 0 0 0-8 0v2H6a2 2 0 0 0-2 2v3l2 2" />
      <circle cx="12" cy="14" r="2" />
      <path d="M12 2v2" />
      <path d="M12 22v-2" />
    </svg>
    <span>
      Developed by <strong style={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>Eng. Maher Madany</strong>
    </span>
  </div>
);

// --- CONFIGURAZIONE STAFF (Fixed Codes) ---
const STAFF_LIST = [
  { code: 'G', name: 'Gaia', pin: '0000', role: 'Coordinatore', color: 'bg-slate-200 text-slate-800' },
  { code: 'M', name: 'MAHER', pin: '1111', role: 'Operatore', color: 'bg-blue-600 text-white' },
  { code: 'Fb', name: 'Fabian', pin: '2222', role: 'Operatore', color: 'bg-orange-800 text-white' },
  { code: 'D', name: 'DANIELE', pin: '3333', role: 'Operatore', color: 'bg-orange-400 text-orange-900' },
  { code: 'C', name: 'Costantino', pin: '4444', role: 'Operatore', color: 'bg-slate-400 text-white' },
  { code: 'E', name: 'Elena', pin: '5555', role: 'Operatore', color: 'bg-indigo-300 text-indigo-900' },
  { code: 'A', name: 'ALFREDO', pin: '6666', role: 'Operatore', color: 'bg-blue-800 text-white' },
  { code: 'X', name: 'Alex', pin: '7777', role: 'Operatore', color: 'bg-red-600 text-white' },
  { code: 'Alla', name: 'Alla', pin: '8888', role: 'Operatore', color: 'bg-green-500 text-white' },
  { code: 'N', name: 'NENAD', pin: '9999', role: 'Operatore', color: 'bg-yellow-400 text-yellow-900' },
];

const ABSENCE_TYPES = ['C-mal', 'Ferie', 'Permesso', 'Recupero'];
const LOG_CATEGORIES = [
  { id: 'Food', label: 'Cena / Alimenti', icon: Utensils, color: 'text-orange-500', bg: 'bg-orange-50' },
  { id: 'Medical', label: 'Terapia Medica', icon: Pill, color: 'text-red-500', bg: 'bg-red-50' },
  { id: 'Security', label: 'Sicurezza / Rientri', icon: ShieldAlert, color: 'text-slate-900', bg: 'bg-slate-100' },
  { id: 'General', label: 'Generale', icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50' },
];

const LOG_TEMPLATES = {
  Food: ["Cena standard: Tutto consumato.", "Cena: Avanzato 1/2 teglia riso.", "Consegna Dussmann regolare."],
  Medical: ["Terapia eseguita regolarmente.", "Somministrato Tachipirina per febbre.", "Residente rifiuta terapia."],
  Security: ["Rientro verificato: tutti presenti.", "Nessuna anomalia riscontrata.", "Segnalato ritardo rientro stanza 4."],
  General: ["Turno regolare.", "Passaggio consegne effettuato.", "Pulizia cucina completata."]
};

export default function App() {
  const [view, setView] = useState('login');
  const [user, setUser] = useState(null);
  const [pin, setPin] = useState('');
  const [shifts, setShifts] = useState(() => JSON.parse(localStorage.getItem('shub_shifts_v2') || '{}'));
  const [absences, setAbsences] = useState(() => JSON.parse(localStorage.getItem('shub_absences') || '{}'));
  const [logs, setLogs] = useState(() => JSON.parse(localStorage.getItem('shub_logs') || '[]'));
  const [selectedCell, setSelectedCell] = useState(null); 
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    localStorage.setItem('shub_shifts_v2', JSON.stringify(shifts));
    localStorage.setItem('shub_absences', JSON.stringify(absences));
    localStorage.setItem('shub_logs', JSON.stringify(logs));
  }, [shifts, absences, logs]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const calculateStats = () => {
    const stats = STAFF_LIST.map(staff => {
      let totalHours = 0;
      Object.entries(shifts).forEach(([key, codes]) => {
        if (Array.isArray(codes) && codes.includes(staff.code)) totalHours += 1;
      });
      return { ...staff, totalHours };
    });
    const absenceStats = ABSENCE_TYPES.reduce((acc, type) => {
      acc[type] = Object.values(absences).filter(v => v === type).length;
      return acc;
    }, {});
    return { stats, absenceStats };
  };

  const { stats, absenceStats } = calculateStats();

  const handleLogin = (digit) => {
    const newPin = pin + digit;
    if (newPin.length <= 4) setPin(newPin);
    if (newPin.length === 4) {
      const foundUser = STAFF_LIST.find(s => s.pin === newPin);
      if (foundUser) {
        setTimeout(() => { setUser(foundUser); setView('dashboard'); setPin(''); }, 200);
      } else {
        setTimeout(() => { alert('PIN ERRATO'); setPin(''); }, 200);
      }
    }
  };

  const addLog = (category, text) => {
    const newLog = {
      id: Date.now(),
      staff: user.name,
      category,
      text,
      time: new Date().toLocaleString('it-IT'),
      isCritical: category === 'Security' || category === 'Medical'
    };
    setLogs([newLog, ...logs]);
  };

  // --- Funzione di eliminazione ---
  const deleteLog = (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questa nota?")) {
      setLogs(logs.filter(log => log.id !== id));
    }
  };

  const toggleStaffInShift = (day, hour, code) => {
    if (user?.role !== 'Coordinatore') return;
    const key = `${day}-${hour}`;
    let current = shifts[key] || [];
    if (!Array.isArray(current)) current = [current];
    if (current.includes(code)) {
      setShifts({ ...shifts, [key]: current.filter(c => c !== code) });
    } else {
      setShifts({ ...shifts, [key]: [...current, code] });
    }
  };

  const updateAbsence = (day, type) => {
    if (user?.role !== 'Coordinatore') return;
    setAbsences({ ...absences, [day]: type });
  };

  const filteredLogs = logs.filter(l => {
    const query = searchQuery.toLowerCase();
    return (l.text || "").toLowerCase().includes(query) || (l.staff || "").toLowerCase().includes(query);
  });

  if (view === 'login') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-slate-900 rounded-[3rem] p-10 shadow-2xl text-center border border-slate-800">
          <div className="w-20 h-20 bg-blue-600 rounded-[1.5rem] flex items-center justify-center mx-auto mb-6"><Lock className="w-10 h-10 text-white" /></div>
          <h1 className="text-3xl font-black text-white mb-8 uppercase tracking-tighter">Saveriani HUB</h1>
          <div className="flex justify-center gap-4 mb-10">
            {[0, 1, 2, 3].map(i => ( <div key={i} className={`w-3.5 h-3.5 rounded-full transition-all ${pin.length > i ? 'bg-blue-500 scale-125 shadow-lg' : 'bg-slate-800'}`}></div> ))}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '←'].map(k => (
              <button key={k} onClick={() => k === 'C' ? setPin('') : k === '←' ? setPin(pin.slice(0, -1)) : handleLogin(String(k))} className="h-16 rounded-2xl bg-slate-800 text-white text-xl font-bold hover:bg-slate-700 active:scale-95 border border-slate-700/50 transition-all">{k}</button>
            ))}
          </div>
        </div>
        <PersonalBadge />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans text-slate-900 overflow-hidden">
      <aside className="w-72 bg-slate-950 text-white flex flex-col p-8 border-r border-slate-900 shrink-0">
        <div className="flex items-center gap-3 mb-12">
          <div className="p-2 bg-blue-600 rounded-lg shadow-lg shadow-blue-500/20"><LayoutDashboard className="w-6 h-6" /></div>
          <h2 className="text-xl font-black uppercase tracking-widest">S-HUB</h2>
        </div>
        <nav className="flex-1 space-y-2">
          {[{ id: 'dashboard', label: 'Panoramica', icon: LayoutDashboard }, { id: 'shifts', label: 'Turni Aprile', icon: Calendar }, { id: 'logbook', label: 'Diario Bordo', icon: BookOpen }, { id: 'stats', label: 'Calcolatore', icon: Calculator }].map(item => (
            <button key={item.id} onClick={() => setView(item.id)} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-bold transition-all ${view === item.id ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-900'}`}>
              <item.icon className="w-5 h-5"/> <span>{item.label}</span>
            </button>
          ))}
        </nav>
        
        {/* User Info */}
        <div className="py-6 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm ${user?.color || 'bg-slate-800'}`}>
              {user?.code || '?'}
            </div>
            <div>
              <p className="font-bold text-sm">{user?.name}</p>
              <p className="text-[10px] text-slate-500 uppercase">{user?.role}</p>
            </div>
          </div>
        </div>
        
        <div className="mt-auto pt-6 border-t border-slate-900 text-center">
           <button onClick={() => { setView('login'); setUser(null); }} className="w-full text-red-500 flex items-center justify-center gap-2 py-3 hover:bg-red-500/10 rounded-xl font-bold text-xs transition-all uppercase tracking-widest"><LogOut className="w-4 h-4" /> Esci</button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="h-24 bg-white border-b border-slate-100 flex items-center justify-between px-10 shrink-0">
          <div>
            <h1 className="text-2xl font-black text-slate-900 uppercase">Saveriani HUB <span className="text-blue-600">v1.3</span></h1>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">{currentTime.toLocaleString('it-IT')}</p>
          </div>
          <button onClick={() => window.print()} className="bg-slate-950 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Report PDF</button>
        </header>

        <div className="flex-1 overflow-y-auto p-10 bg-slate-50/30">
          
          {view === 'dashboard' && (
            <div className="space-y-10">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="p-6 bg-white rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4 action-card card-general">
                  <div className="icon-container bg-general"><Clock className="w-6 h-6"/></div>
                  <div><p className="text-[10px] font-black text-slate-400 uppercase">Ore Mensili</p><h3 className="text-xl font-black">{stats.reduce((acc,s)=>acc+s.totalHours,0)}h</h3></div>
                </div>
                <div className="p-6 bg-white rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4 action-card card-medical">
                  <div className="icon-container bg-medical"><AlertCircle className="w-6 h-6"/></div>
                  <div><p className="text-[10px] font-black text-slate-400 uppercase">Alert Critici</p><h3 className="text-xl font-black">{logs.filter(l=>l.isCritical).length}</h3></div>
                </div>
                <div className="p-6 bg-white rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4 action-card card-food">
                  <div className="icon-container bg-food"><Utensils className="w-6 h-6"/></div>
                  <div><p className="text-[10px] font-black text-slate-400 uppercase">Note Cena</p><h3 className="text-xl font-black">{logs.filter(l=>l.category === 'Food').length}</h3></div>
                </div>
                <div className="p-6 bg-white rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4 action-card card-security">
                  <div className="icon-container bg-security"><ShieldAlert className="w-6 h-6"/></div>
                  <div><p className="text-[10px] font-black text-slate-400 uppercase">Sicurezza</p><h3 className="text-xl font-black">{logs.filter(l=>l.category === 'Security').length}</h3></div>
                </div>
              </div>
              
              {/* Staff Overview */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 action-card">
                <h3 className="text-xl font-black mb-6 flex items-center gap-3">
                  <UserCheck className="w-6 h-6 text-blue-600" /> Staff Operativo
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {STAFF_LIST.map(s => (
                    <div key={s.code} className={`p-4 rounded-2xl text-center transition-all hover:scale-105 ${s.color}`}>
                      <p className="font-black text-lg">{s.code}</p>
                      <p className="text-[10px] font-bold opacity-80">{s.name}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Recent Logs */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 action-card">
                <h3 className="text-xl font-black mb-6 flex items-center gap-3">
                  <BookOpen className="w-6 h-6 text-blue-600" /> Ultime Registrazioni
                </h3>
                <div className="space-y-3">
                  {logs.slice(0, 5).map(log => {
                    const catInfo = LOG_CATEGORIES.find(c => c.id === log.category);
                    const Icon = catInfo?.icon || MessageSquare;
                    return (
                      <div key={log.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${catInfo?.bg}`}>
                          <Icon className={`w-5 h-5 ${catInfo?.color}`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-sm">{log.staff}</p>
                          <p className="text-xs text-slate-500 truncate">{log.text}</p>
                        </div>
                        <span className="text-[10px] text-slate-400">{log.time}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {view === 'shifts' && (
             <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 overflow-hidden">
                <h2 className="text-center font-black mb-8 text-blue-600 text-2xl">PIANO TURNI APRILE 2026</h2>
                
                {/* Staff Legend */}
                <div className="flex flex-wrap gap-2 mb-6 justify-center">
                  {STAFF_LIST.map(s => (
                    <span key={s.code} className={`px-3 py-1 rounded-lg text-xs font-bold ${s.color}`}>
                      {s.code} = {s.name}
                    </span>
                  ))}
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full border-separate border-spacing-0 border border-slate-100 rounded-2xl">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="p-4 border-b border-r text-[10px] font-black uppercase sticky left-0 bg-slate-50 z-10">Ora</th>
                          {[...Array(30)].map((_, i) => ( <th key={i+1} className="p-3 border-b border-r text-[10px] font-black text-center min-w-[75px]">{i+1}</th> ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,0].map(h => (
                          <tr key={h}>
                            <td className="p-3 border-b border-r text-center font-mono text-[10px] font-black sticky left-0 bg-white/90">{String(h).padStart(2,'0')}:00</td>
                            {[...Array(30)].map((_, i) => {
                              const day = i + 1;
                              const key = `${day}-${h}`;
                              const staffCodes = Array.isArray(shifts[key]) ? shifts[key] : (shifts[key] ? [shifts[key]] : []);
                              return (
                                <td key={day} onClick={() => user?.role === 'Coordinatore' && setSelectedCell({day, hour: h, type: 'shift'})} className={`p-1 border-b border-r text-center h-16 hover:bg-blue-50/30 transition-colors ${user?.role === 'Coordinatore' ? 'cursor-pointer' : ''}`}>
                                  <div className="flex flex-wrap gap-1 justify-center items-center">
                                    {staffCodes.map(code => {
                                      const staffInfo = STAFF_LIST.find(s => s.code === code);
                                      return (
                                        <div 
                                          key={code} 
                                          className={`staff-badge text-[11px] font-black rounded-md px-2 py-1 min-w-[28px] text-center shadow-sm ${staffInfo?.color || 'bg-blue-100 text-blue-800'}`}
                                          style={{
                                            letterSpacing: '0.5px',
                                            textShadow: staffInfo?.color?.includes('text-white') ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'
                                          }}
                                        >
                                          {code}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </div>
                
                {user?.role !== 'Coordinatore' && (
                  <p className="text-center text-sm text-slate-400 mt-6">Solo il Coordinatore può modificare i turni</p>
                )}
             </div>
          )}

          {view === 'logbook' && (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-12 animate-in fade-in duration-500">
              <div className="xl:col-span-1 space-y-10">
                <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-slate-100 action-card">
                  <h3 className="text-xl font-black mb-8 tracking-tight">Nuova Registrazione</h3>
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {LOG_CATEGORIES.map(cat => {
                      const Icon = cat.icon;
                      return (
                        <button key={cat.id} onClick={() => { const t = prompt(`Nota per ${cat.label}:`); if(t) addLog(cat.id, t); }} className={`p-6 rounded-[2rem] flex flex-col items-center gap-3 border transition-all hover:scale-105 hover:shadow-lg ${cat.bg} border-transparent`}>
                          <Icon className={`w-8 h-8 ${cat.color}`} />
                          <span className="text-[9px] font-black uppercase text-slate-500">{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Quick Templates */}
                  <div className="space-y-3">
                    <p className="text-[10px] font-black text-slate-400 uppercase">Template Rapidi</p>
                    {Object.entries(LOG_TEMPLATES).map(([cat, templates]) => (
                      <div key={cat} className="space-y-1">
                        {templates.slice(0, 1).map((tmpl, i) => (
                          <button key={i} onClick={() => addLog(cat, tmpl)} className="w-full text-left p-3 bg-slate-50 rounded-xl text-xs text-slate-600 hover:bg-slate-100 transition-all truncate">
                            {tmpl}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="xl:col-span-2 space-y-6">
                <div className="relative mb-8">
                  <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input type="text" placeholder="Cerca note..." className="w-full pl-16 pr-8 py-5 bg-white rounded-[2rem] border border-slate-100 shadow-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" value={searchQuery} onChange={(e)=>setSearchQuery(e.target.value)} />
                </div>
                <div className="space-y-4">
                  {filteredLogs.length === 0 ? (
                    <div className="text-center py-20 text-slate-400">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="font-bold">Nessuna nota trovata</p>
                    </div>
                  ) : filteredLogs.map(log => {
                    const catInfo = LOG_CATEGORIES.find(c => c.id === log.category);
                    const Icon = catInfo?.icon || MessageSquare;
                    return (
                      <div key={log.id} className="p-8 bg-white rounded-[2.5rem] border border-slate-100 shadow-sm flex gap-6 group relative overflow-hidden transition-all hover:shadow-md action-card">
                        <div className={`w-14 h-14 rounded-2xl shrink-0 flex items-center justify-center ${catInfo?.bg}`}>{<Icon className={`w-6 h-6 ${catInfo?.color}`} />}</div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-black text-sm text-slate-900">{log.staff} • <span className="text-blue-600 uppercase text-[10px]">{catInfo?.label}</span></h4>
                            <div className="flex items-center gap-4">
                              <span className="text-[10px] font-bold text-slate-400">{log.time}</span>
                              {/* Delete Button */}
                              <button onClick={() => deleteLog(log.id)} className="p-2 text-slate-300 hover:text-red-500 transition-colors bg-slate-50 rounded-lg hover:bg-red-50" title="Elimina nota">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-slate-600 font-bold italic leading-relaxed text-sm">"{log.text}"</p>
                          {log.isCritical && (
                            <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-600 text-[9px] font-black uppercase rounded">Alert Critico</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {view === 'stats' && (
            <div className="space-y-10">
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm action-card card-general">
                    <h3 className="text-xl font-black mb-8 flex items-center gap-3"><Clock className="w-6 h-6 text-blue-600" /> Riepilogo Ore Staff</h3>
                    <div className="space-y-4">
                      {stats.map(s => (
                        <div key={s.code} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all">
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black ${s.color}`}>{s.code}</span>
                            <span className="font-black text-sm">{s.name}</span>
                          </div>
                          <span className="text-blue-600 font-black text-lg">{s.totalHours} h</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 pt-6 border-t border-slate-100 flex justify-between items-center">
                      <span className="font-black text-slate-500">TOTALE</span>
                      <span className="text-2xl font-black text-blue-600">{stats.reduce((acc, s) => acc + s.totalHours, 0)} h</span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm action-card card-food">
                    <h3 className="text-xl font-black mb-8 flex items-center gap-3"><BarChart3 className="w-6 h-6 text-orange-500" /> Statistiche Assenze</h3>
                    <div className="space-y-4">
                      {ABSENCE_TYPES.map(type => (
                        <div key={type} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                          <span className="font-bold text-sm">{type}</span>
                          <span className="text-orange-500 font-black text-lg">{absenceStats[type] || 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
               </div>
               
               <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm action-card">
                 <h3 className="text-xl font-black mb-8 flex items-center gap-3"><PieChart className="w-6 h-6 text-blue-600" /> Distribuzione Note per Categoria</h3>
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                   {LOG_CATEGORIES.map(cat => {
                     const count = logs.filter(l => l.category === cat.id).length;
                     const Icon = cat.icon;
                     return (
                       <div key={cat.id} className={`p-6 rounded-2xl text-center ${cat.bg}`}>
                         <Icon className={`w-8 h-8 mx-auto mb-2 ${cat.color}`} />
                         <p className="text-3xl font-black">{count}</p>
                         <p className="text-[10px] font-bold text-slate-500 uppercase">{cat.label}</p>
                       </div>
                     );
                   })}
                 </div>
               </div>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {selectedCell && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center z-50 p-6">
            <div className="bg-white rounded-[2.5rem] p-10 max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl font-black mb-2 uppercase text-center">Gestione Turno</h3>
              <p className="text-center text-slate-400 mb-8">Giorno {selectedCell.day} - Ore {String(selectedCell.hour).padStart(2, '0')}:00</p>
              <div className="grid grid-cols-2 gap-3 mb-10">
                {STAFF_LIST.map(s => {
                  const key = `${selectedCell.day}-${selectedCell.hour}`;
                  const current = shifts[key] || [];
                  const isActive = Array.isArray(current) && current.includes(s.code);
                  return (
                    <button key={s.code} onClick={() => toggleStaffInShift(selectedCell.day, selectedCell.hour, s.code)} className={`p-4 rounded-xl font-bold text-xs border-2 transition-all ${isActive ? `${s.color} border-blue-600` : 'border-slate-100 text-slate-400 hover:border-slate-300'}`}>
                      <span className="block text-lg mb-1">{s.code}</span>
                      {s.name}
                    </button>
                  );
                })}
              </div>
              <button onClick={() => setSelectedCell(null)} className="w-full py-4 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all">Chiudi e Salva</button>
            </div>
          </div>
        )}
      </main>
      
      <PersonalBadge />
    </div>
  );
}
